"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import toast from "react-hot-toast";
import ButtonBlueBorder from "@/app/components/appcomponents/ButtonBlueBorder";
import TextFieldComp from "@/app/components/appcomponents/TextFieldComp";
import { PopupShell } from "@/app/components/appcomponents/thankYouWizard/parts";
import guardianService from "@/services/guardian-service";
import orderService from "@/services/order-service";
import { useAuth } from "@/hooks/useAuth";
import SelectField from "./SelectField";
import MemoryPageField from "./MemoryPageField";
import { KEEPER_OPTIONS, KEEPER_REQUEST_SENT, PRICES, euro } from "./orderConfig";

const ALLOWED_DOCUMENTS = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

/**
 * "Postani skrbnik" for a given memory page (it comes from the memory page itself, or from Step 0).
 * - 10 days free: the death document is sent for a manual check; Stripe is not involved.
 * - 1 or 5 years: paid at Stripe; the server makes the buyer Skrbnik once Stripe confirms the payment.
 */
export default function KeeperOrderModal({ obituary, preset = "", onClose }) {
  const { isAuthenticated } = useAuth();
  const [choice, setChoice] = useState(preset);
  const [name, setName] = useState("");
  const [document, setDocument] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const fileInput = useRef(null);

  const isFree = choice === "free";
  const buttonLabel = isFree ? "Pošlji" : choice ? `Naročilo – ${euro(PRICES[choice])}` : "Naročilo";

  // Hard-block: user must be logged in to purchase.
  if (!isAuthenticated) {
    return (
      <PopupShell title="Postani skrbnik" titleInside onClose={onClose}>
        <p className="text-[18px] leading-[26px] text-[#1E2125]">
          Za postati skrbnik se moraš prijaviti.
        </p>
        <div className="mx-auto mt-[32px] w-[250px] mobile:w-full">
          <Link href="/registracija" className="flex h-[44px] w-full items-center justify-center rounded-[4px] bg-gradient-to-b from-[#0D94E8] to-[#0A85C2] text-[16px] text-white shadow-custom-dark-bottom">
            Prijava
          </Link>
        </div>
      </PopupShell>
    );
  }

  const pickDocument = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_DOCUMENTS.includes(file.type)) {
      toast.error("Dovoljene so samo slike in PDF datoteke.");
      e.target.value = "";
      return;
    }
    setDocument(file);
  };

  const submit = async () => {
    if (busy) return;
    if (!isAuthenticated) {
      toast.error("Za oddajo se moraš prijaviti.");
      return;
    }
    if (!choice) {
      toast.error("Izberi Skrbnika");
      return;
    }
    if (!name.trim()) {
      toast.error("Vpiši svoje ime");
      return;
    }
    // Client confirmed: the death document is required for ALL keeper options (free and paid).
    // It prevents fake-death abuse and ensures at least partial cover for paid keepers too.
    if (!document) {
      toast.error("Priloži smrtovnico ali mrliški list");
      return;
    }

    setBusy(true);
    try {
      // The free 10 days are decided by hand, from the document. A buyer who attaches one anyway gets the
      // same application, so the document is not lost.
      if (document) {
        const form = new FormData();
        form.append("name", name.trim());
        form.append("document", document);
        form.append("deceasedName", obituary.name || "");
        form.append("deceasedSirName", obituary.sirName || "");
        form.append("obituaryId", obituary.id);
        form.append("kind", isFree ? "trial" : "paid");
        form.append("product", isFree ? "free" : choice);
        await guardianService.submitGuardianRequest(form);
      }
      if (isFree) {
        setSent(true);
      } else {
        const { paymentUrl } = await orderService.createCheckout({
          product: choice,
          obituaryId: obituary.id,
          displayName: name.trim(),
        });

        if (!paymentUrl) {
          throw new Error(
            "Plačila trenutno ni mogoče pripraviti. Poskusi znova."
          );
        }

        window.location.href = paymentUrl;
        return;
      }
    } catch (error) {
      // The server explains a refusal (page already has a Keeper / trial already used) in its own words.
      const reason = error?.response?.data?.error || error?.message;
      toast.error(reason || "Prišlo je do napake. Poskusi znova.", { duration: 9000 });
    }
    setBusy(false);
  };

  if (sent) {
    return (
      <PopupShell title="Postani skrbnik" titleInside onClose={onClose}>
        <p className="text-[18px] leading-[26px] text-[#1E2125]">
          {KEEPER_REQUEST_SENT}
        </p>
        <div className="mx-auto mt-[32px] w-[250px] mobile:w-full">
          <ButtonBlueBorder placeholder="V redu" onClick={onClose} />
        </div>
      </PopupShell>
    );
  }

  return (
    <PopupShell title="Postani skrbnik" titleInside onClose={onClose}>
      <MemoryPageField obituary={obituary} />

      <div className="mt-[20px]">
        <SelectField label="Izberi Skrbnika" value={choice} onChange={setChoice} options={KEEPER_OPTIONS} />
      </div>

      <div className="mt-[28px] rounded-[10px] bg-[#F0F4F8] px-4 py-6 text-center shadow-custom-dark-to-white">
        <input
          ref={fileInput}
          type="file"
          accept="image/*,application/pdf"
          className="hidden"
          onChange={pickDocument}
        />
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="h-[44px] w-[250px] max-w-full rounded-[4px] bg-gradient-to-b from-[#0D94E8] to-[#0A85C2] text-[16px] text-white shadow-custom-dark-bottom"
        >
          Dodaj dokument
        </button>
        <p className="mt-2 text-[12px] leading-[16px] text-[#939393]">
          {document ?
            document.name
          : <>
              Dokument o smrti, ki ga izda pogrebno podjetje.
              <br />
              To je v korist vseh, ki so izgubili bližnjega.
            </>}
        </p>
      </div>

      <div className="mt-[20px]">
        <TextFieldComp
          placeholder="Tvoje ime"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={22}
        />
      </div>

      <div className="mx-auto mt-[28px] w-[250px] mobile:w-full">
        <ButtonBlueBorder placeholder={buttonLabel} onClick={submit} loading={busy} />
      </div>
    </PopupShell>
  );
}
